import { API_URL } from './config';
import { currentToken } from './auth';

/** Auth + JSON headers from the signed-in session's bearer token. */
function authHeaders(json = false): Record<string, string> {
  const token = currentToken();
  return {
    ...(json ? { 'content-type': 'application/json' } : {}),
    ...(token ? { authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * Typed client for the query API (§6b `POST /query`). These interfaces mirror
 * the server's response contract; the web app stays decoupled from the API
 * package by re-declaring only what it renders.
 */

export type ResultValue = string | number | boolean | null;

export interface ResultColumn {
  key: string;
  label: string;
  kind: 'dimension' | 'time' | 'metric';
}

export type Visualization = 'number' | 'bar' | 'bar_horizontal' | 'line' | 'pie' | 'donut' | 'table' | 'scatter';

export interface QueryPlan {
  entity: string;
  metric: { aggregation: string; field: string | null; label: string };
  group_by: string[];
  joins?: { entity: string; local_field: string; foreign_field: string }[];
  filters: unknown[];
  time_grain: { field: string; grain: string } | null;
  sort: { by: string; direction: string } | null;
  limit: number | null;
  visualization?: Visualization;
}

export interface QueryResult {
  columns: ResultColumn[];
  rows: Record<string, ResultValue>[];
  row_count: number;
  plan: QueryPlan;
  sql: string;
}

/** The "AI Instantly Delivers" layer over a result (server-generated). */
export interface Kpi {
  label: string;
  value: string;
  delta?: string;
  note?: string;
}
export interface TrendInsight {
  direction: 'up' | 'down' | 'flat';
  changePct: number | null;
  narrative: string;
}
export interface RootCauseDriver {
  label: string;
  value: string;
  share: number;
}
export interface RootCause {
  drivers: RootCauseDriver[];
  narrative: string;
}
export interface Insights {
  executiveSummary: string;
  kpis: Kpi[];
  trend: TrendInsight | null;
  rootCause: RootCause | null;
  recommendations: string[];
  source: 'llm' | 'deterministic';
}

export type AskOutcome =
  | {
      ok: true;
      plan: QueryPlan;
      sql: string;
      result: QueryResult | null;
      reason: string;
      assumptions?: string[];
      insights?: Insights | null;
    }
  | { ok: false; stage: 'planning' | 'validation'; reason: string; errors?: string[] }
  | { ok: false; stage: 'clarification'; reason: string; clarification: { question: string; options: string[] } };

/**
 * Which model a query runs against. Omit `model` and the API loads the tenant's
 * ACTIVE model server-side — the normal path, and the reason a question is now a
 * few hundred bytes instead of the whole semantic model (2.5 MB on a large
 * schema, big enough that devtools truncates the payload). Pass `model` only
 * when the caller holds one the server doesn't store — the offline fallback.
 */
export type ModelRef = { model?: unknown };

export interface AskRequest extends ModelRef {
  question: string;
  dataset?: Record<string, Record<string, ResultValue>[]>;
}

export interface StoredModel {
  id: string;
  tenantId: string;
  model: unknown;
  sourceArtifacts: string[];
  generatedAt: string;
  createdAt: string;
}

export interface ModelSummary {
  id: string;
  name: string;
  entityCount: number;
  generatedAt: string;
  createdAt: string;
}

/** Generate + store a semantic model from a spec (build → store). */
export async function generateModel(name: string, spec: string): Promise<StoredModel> {
  const res = await fetch(`${API_URL}/models`, {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify({ name, spec }),
  });
  if (!res.ok) throw new Error(`Generate failed: ${res.status} ${res.statusText}`);
  return (await res.json()) as StoredModel;
}

/** List stored models for the tenant (serve). */
export async function listModels(): Promise<ModelSummary[]> {
  const res = await fetch(`${API_URL}/models`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`List failed: ${res.status}`);
  return (await res.json()) as ModelSummary[];
}

/** The tenant's active model (what the dashboard queries). null when none yet. */
export async function getActiveModel(): Promise<StoredModel | null> {
  const res = await fetch(`${API_URL}/models/active`, { headers: authHeaders() });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Active model fetch failed: ${res.status}`);
  return (await res.json()) as StoredModel;
}

/** Fetch a stored model by id (serve). */
export async function getModel(id: string): Promise<StoredModel> {
  const res = await fetch(`${API_URL}/models/${id}`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
  return (await res.json()) as StoredModel;
}

/** POST a question to the query API, normalizing transport errors into an outcome. */
export async function ask(req: AskRequest): Promise<AskOutcome> {
  try {
    const res = await fetch(`${API_URL}/query`, {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify(req),
    });
    if (!res.ok) {
      return { ok: false, stage: 'planning', reason: `API error ${res.status}: ${res.statusText}` };
    }
    return (await res.json()) as AskOutcome;
  } catch (err) {
    return {
      ok: false,
      stage: 'planning',
      reason: `Could not reach the API at ${API_URL}. Is it running (make dev)? ${(err as Error).message}`,
    };
  }
}

/**
 * Analyst-quality question suggestions from the API (LLM-generated, grounded in the
 * model). Returns `[]` on error or when the LLM is unavailable, so the caller can
 * fall back to the local heuristic. `refresh` asks for a fresh set.
 */
export async function fetchSuggestions(ref: ModelRef, refresh = false): Promise<string[]> {
  try {
    const res = await fetch(`${API_URL}/query/suggestions`, {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify({ ...ref, refresh }),
    });
    if (!res.ok) return [];
    return ((await res.json()) as { questions?: string[] }).questions ?? [];
  } catch {
    return [];
  }
}

/** Entities that can be queried live (have a data-source endpoint). Empty on error. */
export async function queryableEntities(): Promise<string[]> {
  try {
    const res = await fetch(`${API_URL}/query/entities`, { headers: authHeaders() });
    if (!res.ok) return [];
    return ((await res.json()) as { entities: string[] }).entities ?? [];
  } catch {
    return [];
  }
}

interface LiveResponse {
  ok: boolean;
  reason?: string;
  plan?: QueryPlan;
  sql?: string;
  points?: { label: string; value: number }[];
  dimension?: string | null;
  dimensions?: string[];
  rows?: Record<string, ResultValue>[];
  timeKey?: string | null;
  metricLabel?: string;
  /** Measure columns (row key + label), primary first. One entry for a single measure. */
  measures?: { key: string; label: string }[];
  /** Defaults the planner filled in for an underspecified question. */
  assumptions?: string[];
  /** AI deliverables over the live result. */
  insights?: Insights | null;
  /** Present when the ask was too ambiguous — a question + options to put to the user. */
  clarification?: { question: string; options: string[] };
}

/**
 * Ask a LIVE question: the server plans it, runs it as an edge aggregation on the
 * tenant's connector (raw rows never leave the client network), and returns the
 * summary. We adapt that summary into the same {@link QueryResult} shape the demo
 * path uses, so the chart/table/SQL renderer is identical.
 */
export async function askLive(question: string, ref: ModelRef, opts?: { insights?: boolean }): Promise<AskOutcome> {
  try {
    const res = await fetch(`${API_URL}/query/live`, {
      method: 'POST',
      headers: authHeaders(true),
      // `insights: false` skips the LLM narrative — dashboard tiles don't render it,
      // so it's a big per-tile latency saving. Ask omits it to keep the default.
      body: JSON.stringify({ question, ...ref, ...(opts?.insights === false ? { insights: false } : {}) }),
    });
    const body = (await res.json()) as LiveResponse;
    if (!res.ok) {
      return { ok: false, stage: 'planning', reason: (body as { message?: string }).message ?? `API error ${res.status}` };
    }
    if (!body.ok) {
      if (body.clarification) {
        return { ok: false, stage: 'clarification', reason: body.reason ?? '', clarification: body.clarification };
      }
      return { ok: false, stage: 'planning', reason: body.reason ?? 'Could not answer that.' };
    }

    const dims = body.dimensions ?? (body.dimension ? [body.dimension] : []);
    const columns: ResultColumn[] = dims.map((d) => ({
      key: d,
      label: d,
      kind: d === body.timeKey ? ('time' as const) : ('dimension' as const),
    }));
    // One metric column per measure (a single measure keeps the 'value' key).
    const measures = body.measures ?? [{ key: 'value', label: body.metricLabel ?? 'Value' }];
    for (const m of measures) columns.push({ key: m.key, label: m.label, kind: 'metric' });
    // Prefer the full rows (multi-dimension/measure); fall back to single-dimension points.
    const rows =
      body.rows ??
      (body.points ?? []).map((p) => (dims[0] ? { [dims[0]]: p.label, value: p.value } : { value: p.value }));
    const plan = body.plan!;
    // A time bucket + exactly one category is a MULTI-SERIES line (one line per
    // category value) — keep it as a line. Any other ≥2-dimension result, or any
    // multi-measure result (a chart shows one measure), renders as a table.
    const isMultiSeriesLine = !!body.timeKey && dims.length === 2 && plan.visualization === 'line';
    if (measures.length >= 2) {
      plan.visualization = 'table';
    } else if (dims.length >= 2 && !isMultiSeriesLine && plan.visualization !== 'table') {
      plan.visualization = 'table';
    }
    const result: QueryResult = { columns, rows, row_count: rows.length, plan, sql: body.sql ?? '' };
    return { ok: true, plan, sql: body.sql ?? '', result, reason: 'live', assumptions: body.assumptions ?? [], insights: body.insights };
  } catch (err) {
    return {
      ok: false,
      stage: 'planning',
      reason: `Could not reach the API at ${API_URL}. ${(err as Error).message}`,
    };
  }
}
